import { addDays, formatLocalDate, getWeekStart, parseLocalDate } from '../product/productUtils';

// Shared by the Content plan, its Ideas panel and the dashboard: best times to
// post (from the server's click data), winners (posts that beat a target) and
// where a new post fits in a week.

export const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const METRICS = ['views', 'likes', 'clicks'];
export const METRIC_LABELS = { views: 'views', likes: 'likes', clicks: 'clicks' };

// When nothing better is known: commonly recommended times per platform
const PLATFORM_DEFAULT_TIMES = [
    [/tiktok/i, '7:00 PM'],
    [/instagram/i, '6:00 PM'],
    [/youtube/i, '5:00 PM'],
    [/\bx\b|twitter|threads|bluesky/i, '9:00 AM'],
    [/linkedin/i, '8:00 AM'],
    [/facebook/i, '1:00 PM'],
    [/pinterest/i, '8:00 PM'],
];
const FALLBACK_TIME = '12:00 PM';

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const toText = (value) => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');
const toNumber = (value) => {
    if (value === '' || value == null) return null;
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.round(number)) : null;
};
const samePlatform = (a, b) => {
    const x = toText(a).trim().toLowerCase();
    const y = toText(b).trim().toLowerCase();
    return Boolean(x && y) && (x === y || x.startsWith(`${y} `) || y.startsWith(`${x} `));
};

export const browserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
        return '';
    }
};

/* ---------- Best times ---------- */

const normalizeSlot = (slot) => {
    if (!isObject(slot) || !WEEK_DAYS.includes(slot.day)) return null;
    const hour = toNumber(slot.hour);
    if (hour == null || hour > 23) return null;
    const time = toText(slot.time) || `${hour % 12 || 12}:00 ${hour < 12 ? 'AM' : 'PM'}`;
    return { day: slot.day, hour, time, label: toText(slot.label) || `${slot.day.slice(0, 3)} ${time.replace(':00', '')}`, clicks: toNumber(slot.clicks) || 0 };
};

const normalizeSlots = (slots) => (Array.isArray(slots) ? slots : []).map(normalizeSlot).filter(Boolean).slice(0, 3);

// The server's answer in a shape that's safe to render; null when it isn't one
export const normalizeBestTimes = (raw) => {
    if (!isObject(raw)) return null;
    const platforms = (Array.isArray(raw.platforms) ? raw.platforms : [])
        .filter(isObject)
        .map(group => ({ platform: toText(group.platform) || 'Other', clicks: toNumber(group.clicks) || 0, slots: normalizeSlots(group.slots) }))
        .filter(group => group.slots.length);
    const overall = normalizeSlots(raw.overall);
    return {
        enough: raw.enough === true && (platforms.length > 0 || overall.length > 0),
        clicks: toNumber(raw.clicks) || 0,
        minimum: toNumber(raw.minimum) || 20,
        timezone: toText(raw.timezone),
        platforms,
        overall,
    };
};

// The platform's own best slots, else every platform's
export const bestSlotsFor = (bestTimes, platform) => {
    if (!bestTimes?.enough) return [];
    const own = bestTimes.platforms.find(group => samePlatform(group.platform, platform));
    return own ? own.slots : bestTimes.overall;
};

// The time the plan already uses most for the platform, else a common default
const usualTime = (posts, platform) => {
    const counts = {};
    posts.filter(post => samePlatform(post.platform, platform) && toText(post.time)).forEach(post => {
        counts[post.time] = (counts[post.time] || 0) + 1;
    });
    const [best] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (best) return best[0];
    return (PLATFORM_DEFAULT_TIMES.find(([pattern]) => pattern.test(toText(platform))) || [null, FALLBACK_TIME])[1];
};

// Where a new post goes in a week: { day, time }. Days before `today` are skipped.
// Prefers the platform's best-time days, then any day without a post, then (unless
// `strict`) a day without a post on this platform, then the first open day.
// `preferredDay` (e.g. a calendar date) wins when it's still ahead. Null when no day fits.
export const chooseSlot = ({ weekStart, posts = [], platform = '', bestTimes = null, today = formatLocalDate(new Date()), strict = false, preferredDay = '' }) => {
    const list = Array.isArray(posts) ? posts.filter(isObject) : [];
    const open = WEEK_DAYS.filter((day, index) => addDays(weekStart, index) >= today);
    if (!open.length) return null;
    const slots = bestSlotsFor(bestTimes, platform);
    const free = (day) => !list.some(post => post.day === day);
    const freeForPlatform = (day) => !list.some(post => post.day === day && samePlatform(post.platform, platform));
    const slotDays = slots.map(slot => slot.day).filter(day => open.includes(day));
    const day = (preferredDay && open.includes(preferredDay) && preferredDay)
        || slotDays.find(free)
        || open.find(free)
        || (strict ? null : slotDays.find(freeForPlatform) || open.find(freeForPlatform) || open[0]);
    if (!day) return null;
    const slot = slots.find(item => item.day === day);
    return { day, time: slot ? slot.time : usualTime(list, platform) };
};

/* ---------- Weeks and dates ---------- */

// The Monday of a YYYY-MM-DD date and its weekday name
export const weekOfDate = (date) => {
    const weekStart = getWeekStart(parseLocalDate(date));
    const index = Math.round((parseLocalDate(date) - parseLocalDate(weekStart)) / 86400000);
    return { weekStart, day: WEEK_DAYS[Math.min(6, Math.max(0, index))] };
};

export const formatDayOfWeek = (weekStart, day) => {
    const index = Math.max(0, WEEK_DAYS.indexOf(day));
    return parseLocalDate(addDays(weekStart, index)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

export const formatShortDay = (date) => {
    const parsed = parseLocalDate(date);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* ---------- Winners ---------- */

// { [postId]: ['views', ...] } for posted posts that reached a target (> 0).
// `getActual(post)` gives { views, likes, clicks } (null = not logged); the
// server's own list (`serverWinners`, from results stored at the last review) is added.
export const findWinners = (posts, getActual, serverWinners = []) => {
    const result = {};
    (Array.isArray(posts) ? posts : []).filter(post => isObject(post) && post.status === 'posted' && toText(post.id)).forEach(post => {
        const actual = (typeof getActual === 'function' && getActual(post)) || {};
        const metrics = METRICS.filter(metric => {
            const target = toNumber(post.targets?.[metric]);
            const value = toNumber(actual[metric]);
            return target > 0 && value != null && value >= target;
        });
        if (metrics.length) result[post.id] = metrics;
    });
    (Array.isArray(serverWinners) ? serverWinners : []).filter(isObject).forEach(winner => {
        const id = toText(winner.postId);
        const post = id && (posts || []).find(item => isObject(item) && item.id === id && item.status === 'posted');
        if (!post) return;
        const metrics = (Array.isArray(winner.metrics) ? winner.metrics : []).filter(metric => METRICS.includes(metric));
        if (!metrics.length) return;
        result[id] = METRICS.filter(metric => metrics.includes(metric) || (result[id] || []).includes(metric));
    });
    return result;
};

// "views and clicks"
export const listMetrics = (metrics) => {
    const names = metrics.map(metric => METRIC_LABELS[metric] || metric);
    return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};
